import { Component, OnInit, ViewChild } from '@angular/core';
import { DataService, MigrateContractorRegistrationsResult, ContractorRegistrationMigrationProcessing, WorkflowContextService } from 'src/app/services';
import { Client, ContractorType, Document } from 'src/app/models';
import {
  UploadComponent,
  UploadResponse
} from 'src/app/components/upload/upload.component';

@Component({
  selector: 'wm-contractor-registration-migration',
  templateUrl: './contractor-registration-migration.component.html',
  styleUrls: ['./contractor-registration-migration.component.css'],
  standalone: false
})
export class ContractorRegistrationMigrationComponent implements OnInit {
  @ViewChild('migrationUploadComponent', { static: false })
  migrationUploadComponent!: UploadComponent;

  uploadKey: string = '';
  migrationFilePath: string | null = null;
  migrationFileUploaded: boolean = false;

  selectedClientId: string | null = null;
  clients: Client[] = [];
  loadingClients: boolean = false;

  selectedContractorTypeId: string | null = null;
  contractorTypes: ContractorType[] = [];
  loadingContractorTypes: boolean = false;

  loading: boolean = false;
  error: string | null = null;
  success: boolean = false;

  migrationResult: MigrateContractorRegistrationsResult | null = null;

  // Migration history
  migrationHistory: ContractorRegistrationMigrationProcessing[] = [];
  loadingHistory: boolean = false;

  constructor(
    private _dataSvc: DataService,
    private _context: WorkflowContextService
  ) {}

  ngOnInit() {
    this.uploadKey = this.generateId();
    this.loadClients();
  }

  loadClients() {
    this.loadingClients = true;
    this._dataSvc.getClients().subscribe({
      next: clients => {
        this.clients = clients.sort((a, b) => a.name.localeCompare(b.name));
        this.loadingClients = false;
      },
      error: err => {
        this.error =
          'Failed to load jurisdictions: ' + (err.error?.message || err.message);
        this.loadingClients = false;
      }
    });
  }

  onClientChange() {
    this.selectedContractorTypeId = null;
    this.contractorTypes = [];
    this.migrationHistory = [];
    if (!this.selectedClientId) return;

    this.loadingContractorTypes = true;
    this._dataSvc.getContractorTypes(this.selectedClientId).subscribe({
      next: types => {
        this.contractorTypes = (types ?? []).sort((a, b) =>
          (a.name ?? '').localeCompare(b.name ?? '')
        );
        this.loadingContractorTypes = false;
      },
      error: err => {
        this.error =
          'Failed to load contractor types: ' + (err.error?.message || err.message);
        this.loadingContractorTypes = false;
      }
    });

    this.loadHistory();
  }

  loadHistory() {
    if (!this.selectedClientId) return;
    this.loadingHistory = true;
    this._dataSvc.getContractorRegistrationMigrationProcessings(this.selectedClientId).subscribe({
      next: records => {
        this.migrationHistory = records ?? [];
        this.loadingHistory = false;
      },
      error: () => { this.loadingHistory = false; }
    });
  }

  onFileUploaded(response: UploadResponse) {
    this.migrationFilePath = response.path;
    this.migrationFileUploaded = true;
    this.error = null;
    this.migrationResult = null;
  }

  onFileDeleted(_document: Document) {
    this.migrationFilePath = null;
    this.migrationFileUploaded = false;
    this.migrationResult = null;
  }

  migrate() {
    if (!this.migrationFileUploaded) {
      this.error = 'Please upload a contractor registration migration file.';
      return;
    }
    if (!this.selectedClientId) {
      this.error = 'Please select a jurisdiction.';
      return;
    }
    if (!this.selectedContractorTypeId) {
      this.error = 'Please select a contractor type.';
      return;
    }

    this.loading = true;
    this.error = null;
    this.success = false;
    this.migrationResult = null;

    this._dataSvc
      .migrateContractorRegistrations(
        this.migrationFilePath!,
        this.selectedClientId,
        this.selectedContractorTypeId
      )
      .subscribe({
        next: result => {
          this.loading = false;
          this.success = true;
          this.migrationResult = result;
          this.loadHistory();
        },
        error: err => {
          this.loading = false;
          this.error =
            'Contractor registration migration failed: ' +
            (err.error?.message || err.message);
          this.loadHistory();
        }
      });
  }

  getWarnings(record: ContractorRegistrationMigrationProcessing): string[] {
    if (!record.warningsJson) return [];
    try { return JSON.parse(record.warningsJson); } catch { return []; }
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2, 11);
  }
}

